import * as LocaleUtil from "@eres/core/dist/LocaleValue";
import { LocaleValue } from "@eres/core/dist/LocaleValue";
import { LVStrings } from "@my-dld/assets/strings";
import { Property, RVProperty } from "./Property";
import { RVLookupX } from "./api";
import { Lookup } from "@eres/ejari-api/Entity";

export enum RVMode {
	OWN = 1,
	TENANT = 2,
	POA = 3,
	MANAGED = 4,
}

export enum RVPropertyType {
	LAND = 0,
	VILLA = 4,
	UNIT = 2,
	BUILDING = 1,
}

export enum RVSearchType {
	LandNumber = 1,
	DMNumber = 2,
	EjariNumber = 3,
	TitleDeed = 4,
	Dewa = 5,
	Property = 6,
	Participant = 7,
}

export enum RVPropView {
	RV_Properties = 1,
	Tabu_Properties = 2,
	RV_REQ_Properties = 3,
}

export interface RVPicketItem extends LocaleUtil.LocaleValue {
	Identity: number;
}

export const EvalMode: RVPicketItem[] = [
	{
		Identity: RVMode.OWN,
		...LVStrings.on_my_property,
	},
	{
		Identity: RVMode.TENANT,
		...LVStrings.on_my_leased_property,
	},
	{
		Identity: RVMode.POA,
		...LVStrings.on_behalf_of,
	},
	{
		Identity: RVMode.MANAGED,
		...LVStrings.managed_properties,
	},
];

export const PTypes: RVPicketItem[] = [
	{
		Identity: RVPropertyType.UNIT,
		...LVStrings.unit,
	}, {
		Identity: RVPropertyType.VILLA,
		...LVStrings.villa,
	}, {
		Identity: RVPropertyType.BUILDING,
		...LVStrings.building,
	}, {
		Identity: RVPropertyType.LAND,
		...LVStrings.land,
	},
];

export const SearchMode: RVPicketItem[] = [
	{
		Identity: RVSearchType.Participant,
		...LVStrings.owned_properties,
	},
	{
		Identity: RVSearchType.LandNumber,
		...LVStrings.land_no,
	},
	{
		Identity: RVSearchType.DMNumber,
		...LVStrings.dm_number,
	},
	{
		Identity: RVSearchType.EjariNumber,
		...LVStrings.contract_no,
	},
	{
		Identity: RVSearchType.TitleDeed,
		...LVStrings.title_deed,
	},
	{
		Identity: RVSearchType.Dewa,
		...LVStrings.dewaNo,
	},
];

export interface SearchOption {
	participantId?: number;
	userTypeId: number;
	propertyNumber?: string;
	buildingName?: string;
	dewaPremise?: string;
	propertyTypeId: RVPropertyType;
	searchedPropertyTypeId?: RVPropertyType;
	areaId?: number;
	dmNo?: string;
	dmSubNo?: string;
	managementCompanyId: number;
	searchTypeId: RVSearchType;
	startIndex: number;
	maxRows: number;
	landNo?: string;
	landSubNo?: string;
	certificateNumber?: string;
	certificateYear?: string;
	requestorTypeId: RVMode;
	isEjariUser: number;
	contractNumber?: string;
	emiratesId?: string;
	propertyId?: string;
	isFreehold?: 1 | 0;
}

export interface RVEnvironment {
	isEjariUser: boolean;
	isManagementCompany?: boolean;
	participantId?: number;
	managementId?: number;
	userTypeId?: number;
	mode?: RVMode;
	propertyType?: RVPropertyType;
	uidNumber?: string;
	eidNumber?: string;
	phoneNumber?: string;
	editableEid: boolean;
	editablePhone: boolean;
}

export interface PossibleErrorResponse {
	ValidationErrors?: number[];
	Error?: number[];
}

export interface PropertiesResponse extends PossibleErrorResponse {
	PropertiesList?: Property[];

}

export interface RVResponse<T> {
	Code: string;
	Message: string;
	Response: T;
	ExceptionId?: string;
	Errors?: { Code: number; Message: { En?: string, Ar?: string } }[];
}

export interface Attachment {
	ActualSizeKb?: number;
	DocumentId?: number;
	FileUrl?: string;
	Identity?: number;
	ParentObjectId?: number;
	PhysicalName?: string;
	TemporaryRequestId: string;
}

export interface RequestorProfile {
	Email: string;
	EmiratesId: string;
	Identity: number;
	IsEjariUser: number;
	IsOwnerCompany: number;
	LicenseNumber: string;
	ManagementCompId: number;
	MangementCompNameAr: string;
	MangementCompNameEn: string;
	MobileNumber: string;
	PartNameAr: string;
	PartNameEn: string;
	ParticipantId: number;
	RequestId: number;
	UserAccountId: number;
	UserProfileId: number;
	UnifiedIdNumber?: string;
}

export interface RVRequest {
	RequestorTypeId: RVMode;
	Attachments: Attachment[];
	Identity?: number;
	Properties: RVProperty[];
	RequestChannelId: number;
	RequestDescription: string;
	RequestOn?: string;
	RequestReasonId: number;
	RequestStatusId?: number;
	RequestorProfile: RequestorProfile;
	RequestId?: number;
	RequestNumber?: string;
	RequestedBySSOId?: number;
	RequetedByParticipantId?: number;
	RequestStatusNameAr?: string;
	RequestStatusNameEn?: string;
	RequestReasonNameAr?: string;
	RequestReasonNameEn?: string;
}

export interface PropertyPayment {
	EjariSubTypeId: number;
	EjariSubTypeIdNameAr: string;
	EjariSubTypeIdNameEn: string;
	EjariTypeId: number;
	EjariTypeIdNameAr: string;
	EjariTypeIdNameEn: string;
	EjariUsageId: number;
	EjariUsageIdNameAr: string;
	EjariUsageIdNameEn: string;
	FeeAmount: number;
	Identity: number;
	IsPerSqmSize: number;
	PaymentStructureId: number;
	PropertyTypeId: number;
}

export interface AuditedRVRequest extends RVRequest {
	AuditRejectionReasonId?: number;
	AuditStatusId?: number;
	Status?: LocaleValue;
	AuditedBy?: number;
	AuditedByNameAr?: string;
	AuditedByNameEn?: string;
	AuditedOn?: string;
	AuditorRemarks?: string;
	CappedAmount?: number;
	IsLinked?: number;
	PaidOn?: string;
	PaymentReferenceNumber?: string;
	TotalAmount?: number;
	AuditStatusNameAr?: string;
	AuditStatusNameEn?: string;
	AuditRejectionReasonNameAr?: string;
	AuditRejectionReasonNameEn?: string;
}

export interface RVSubmitResponse extends PossibleErrorResponse {
	IsSuccess: boolean;
	Message: string;
	RequestId: number;
	RequestNumber: string;
}

export const defProfile: Partial<RequestorProfile> = {
	IsEjariUser: 0,
	IsOwnerCompany: 0,
	LicenseNumber: "",
	ManagementCompId: 0,
	MangementCompNameAr: undefined,
	MangementCompNameEn: undefined,
	MobileNumber: undefined,
	PartNameAr: undefined,
	PartNameEn: undefined,
	ParticipantId: undefined,
	RequestId: 0,
	UserAccountId: 0,
	UserProfileId: 0,
};

export interface RVSearchQuery {
	AreaId?: number;
	BuildingName?: string;
	DMNo?: string;
	DMSubNo?: string;
	EjariContractNumber?: string;
	IsOwnerCompany: 0 | 1;
	LandNo?: string;
	LandSubNo?: string;
	ManagementCompId?: number;
	PageIndex: number;
	PageSize: number;
	ParticipantId?: number;
	RequestFromDate?: string;
	RequestNumber?: string;
	RequestStatusId?: number;
	RequestToDate?: string;
	RequestorEmail?: string;
	RequestorEmiratesId?: string;
	RequestorLicenseNumber?: string;
	RequestorMobileNumber?: string;
	RequestorName?: string;
	RequestorTypeId: number;
	UnitNumber?: string;
}

export interface RVFeeItem {
	Amount: number;
	DefinedFeesAmount: number;
	Description: string;
	FeeCode: string;
	FeeID: number;
	FeeNameAr: string;
	FeeNameEn: string;
	FeeType: number;
}

export interface MCInfo {
	NameEn: string;
	NameAr: string;
	LicenseNumber: string;
	LicenseIssueDate?: string;
	LicenseExpiryDate?: string;
	Address?: string;
	Email?: string;
	Fax?: string;
	Phone?: string;
	PoBox?: string;
	ContactPersonNameAr: string;
	ContactPersonNameEn: string;
	ContactPersonLastNameAr?: string;
	ContactPersonLastNameEn?: string;
	ContactPersonMobile?: string;
	Identity: number;
}

interface LookupTransformer {
	( input: RVLookupX ): Lookup;
}

export const RVLookUpTransformer: LookupTransformer = ( input: RVLookupX ) => {
	return {
		ar: input.NameAr,
		en: input.NameEn,
		Id: Number( input.Identity ),
	} as Lookup;
};

export enum RVRequestStatus {
	SUBMITTED = 1,
	PAYMENT_PENDING = 2,
	EVALUATION_PENDING = 3,
	COMPLETED = 4,
	CANCELLED = 5,
}
